import React, { useEffect, useState } from "react";
import { addRepos } from "../../services/service";
import { colors } from "../../colors";
import ConfirmationDialogue from "../../common/ConfirmationDialogue";
import Logger from "../../utils/logger";
import { useUser } from "../../services/useUser";

type Repo = {
  name: string;
  type: "api" | "ui" | "mob";
};

export type DeploymentObject = {
  group: string;
  repo: Repo[];
  dbRepo: DbRepo[];
  requester: string;
};

type DbRepo = {
  name: string;
  type: "postgres" | "mysql" | "mongodb" | "redis" | "dynamodb";
};

const ProjectRequestForm: React.FC = () => {
  const [group, setGroup] = useState<string>("");
  const [repo, setRepo] = useState<Repo[]>([]);
  const [repoName, setRepoName] = useState<string>("");
  const [repoType, setRepoType] = useState<"api" | "ui" | "mob">("api");
  const [dbRepo, setDbRepo] = useState<DbRepo[]>([]);
  const [dbRepoName, setDbRepoName] = useState<string>("");
  const [dbRepoType, setDbRepoType] = useState<
    "postgres" | "mysql" | "mongodb" | "redis" | "dynamodb"
  >("mysql");

  const [showLoader, setShowLoader] = useState<boolean>(false);
  const [requester, setRequester] = useState<string>("");
  const [requesterDisplayName, setRequesterDisplayName] = useState<string>("");

  const [showConfirmation, setShowConfirmation] = useState(false);
  const user = useUser();

  const addRepo = () => {
    if (repoName) {
      setRepo([...repo, { name: repoName, type: repoType }]);
      setRepoName(""); // Reset the repo name input
    }
  };

  const addDbRepo = () => {
    if (dbRepoName) {
      setDbRepo([...dbRepo, { name: dbRepoName, type: dbRepoType }]);
      setDbRepoName(""); // Reset input
    }
  };

  const handleSubmit = () => {
    setShowLoader(true);
    // Add basic validation
    if (!group || !requester || repo.length === 0) {
      alert(
        "Please fill in all required fields (Group, Repos, Project Requester)."
      );
      setShowLoader(false);
      return;
    }
    setShowConfirmation(true);
  };

  const confirmSubmit = async () => {
    const deploymentData: DeploymentObject = {
      group,
      repo,
      dbRepo,
      requester,
    };

    try {
      Logger.info("Submitting deployment data", { deploymentData });
      const response = await addRepos(deploymentData);
      // Handle success (e.g., show success message, close modal)
      if (response.status === 201) {
        setGroup("");
        setRepo([]);
        setDbRepo([]);
        setRequester("");
        alert(
          `Request data submitted successfully! Ticket No: ${response.data}`
        );
      } else {
        throw new Error("Error submitting deployment object");
      }
    } catch (error) {
      Logger.error("Error submitting deployment object:", error);
      alert("Error submitting data.");
    } finally {
      setShowConfirmation(false);
      setShowLoader(false);
    }
  };

  useEffect(() => {
    const name = user?.attributes?.name;
    const email = user?.attributes?.email;
    setRequester(email);
    setRequesterDisplayName(name);
  }, []);

  return (
    <div className="flex gap-4">
      <div className="flex-col gap-2">
        {/* Project Requester Input */}
        <label className="block mb-2 font-semibold">Requester Name</label>
        <input
          type="text"
          className="w-full mb-4 p-2 border rounded-md"
          value={requesterDisplayName}
          readOnly
        />

        {/* Group Input */}
        <label className="block mb-2 font-semibold">Project Name</label>
        <input
          type="text"
          value={group}
          onChange={(e) => setGroup(e.target.value)}
          className="w-full mb-4 p-2 border rounded-md"
          placeholder="Enter project group name"
        />

        {/* Repo Inputs */}
        <div className="mb-4">
          <h2 className="font-semibold mb-2">Repositories</h2>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={repoName}
              onChange={(e) => setRepoName(e.target.value)}
              className="flex-grow p-2 border rounded-md"
              placeholder="Repo Name"
            />
            <select
              value={repoType}
              onChange={(e) =>
                setRepoType(e.target.value as "api" | "ui" | "mob")
              }
              className="px-2 py-3 text-[13px] border rounded-md"
            >
              <option value="api">API</option>
              <option value="ui">UI</option>
              <option value="mob">MOB</option>
            </select>
            <button
              onClick={addRepo}
              className="p-2 rounded-md"
              style={{
                backgroundColor: colors.filterButtonsUnActive,
                color: colors.filterButtonsText,
              }}
            >
              Add Repo
            </button>
          </div>
        </div>

        {/* DB Repo Inputs */}
        <div className="mb-4">
          <h2 className="font-semibold mb-2">DB Repositories</h2>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={dbRepoName}
              onChange={(e) => setDbRepoName(e.target.value)}
              className="flex-grow p-2 border rounded-md"
              placeholder="DB Repo Name"
            />
            <select
              value={dbRepoType}
              onChange={(e) =>
                setDbRepoType(
                  e.target.value as
                    | "postgres"
                    | "mysql"
                    | "mongodb"
                    | "redis"
                    | "dynamodb"
                )
              }
              className="px-2 py-3 text-[13px] border rounded-md"
            >
              <option value="mysql">MySQL</option>
              <option value="postgres">PostgreSQL</option>
              <option value="mongodb">MongoDB</option>
              <option value="redis">Redis</option>
              <option value="DynamoDB">DynamoDB</option>
            </select>
            <button
              onClick={addDbRepo}
              className="p-2 rounded-md"
              style={{
                backgroundColor: colors.filterButtonsUnActive,
                color: colors.filterButtonsText,
              }}
            >
              Add DB Repo
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex gap-2 grow">
          <div className="p-4 shadow rounded">
            <h3 className="border-b mb-2 font-semibold">Requesting Repos</h3>
            {/* List of Added Repos */}
            {repo.length > 0 && (
              <ul className="mt-2">
                {repo.map((r, idx) => (
                  <li key={idx} className="mb-1">
                    {r.name} ({r.type})
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="p-4 shadow rounded">
            <h3 className="border-b mb-2 font-semibold">Requesting DB Repos</h3>
            {dbRepo.length > 0 && (
              <ul className="mt-2">
                {dbRepo.map((db, idx) => (
                  <li key={idx} className="mb-1">
                    {db.name} ({db.type})
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          className={`w-full py-2 px-6 rounded-md ${
            showLoader ? "cursor-not-allowed" : ""
          }`}
          style={{
            backgroundColor: colors.filterButtonsActive,
            color: colors.filterButtonsText,
          }}
          disabled={showLoader}
        >
          {showLoader ? "Please Wait" : "Submit Request"}
        </button>
      </div>

      {/* Confirmation Modal */}
      {showConfirmation && (
        <ConfirmationDialogue
          setShowConfirmation={setShowConfirmation}
          setShowLoader={setShowLoader}
          confirmSubmit={confirmSubmit}
          confirmationMessage="Are you sure you want to submit this data?"
        />
      )}
      {JSON.stringify(user, null, 2)}
    </div>
  );
};

export default ProjectRequestForm;
